import Card from 'assets/components/card/Card';
import ThemeCardListItem from 'assets/components/card/themes/Theme.Card.ListItem';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { formatCurrency, formatSignedCurrency, roundToStep } from 'assets/utils/data/Number';
import { isNil } from 'lodash';
import React from 'react';
import { ExcludedAmount } from './ExcludedAmount';

type TableData = Api.Sales.ContractRefund.RefundPricingInfo.RefundPriceInfo & {
  excludeAdministrationFee?: boolean;
  refundConfigTemplateJson?: Model.IRefundConfigTemplate;
};
type RowType = 'dealerCost' | 'dealerASCProfit' | 'dealerProfit' | 'retailSalePrice';
type TableProps = {
  data: TableData;
  actions?: Partial<Record<RowType, React.ReactNode>>;
  onAdminFeeExclusion?(data: { excludeAdministrationFee: TableData['excludeAdministrationFee'] }): void;
};

export const ContractRefundSummariesTable = ({ data, actions, onAdminFeeExclusion }: TableProps) => {
  const { lang } = getLocales();
  // Old formula: DealerProtectProfitAmount + (Dealer Profit Amount - DealerProtectProfitAmount) * Proration Factor
  // const protectedAmount = data.refundConfigTemplate?.dealerProtectProfitAmount;
  // const dealerProtectProfitExpirationDays = data.refundConfigTemplate?.dealerProtectProfitExpirationDays;
  // const shouldShowDealerProfitSplit =
  //   !isNil(protectedAmount) &&
  //   !isNil(dealerProtectProfitExpirationDays) &&
  //   data.usedTermInDays >= dealerProtectProfitExpirationDays;
  // const dealerProfitAfterProtection = isNil(protectedAmount)
  //   ? undefined
  //   : Math.max(data.dealerProfitValueAtSale - protectedAmount, 0);
  // const proratedRetainedAmount = isNil(dealerProfitAfterProtection)
  //   ? undefined
  //   : dealerProfitAfterProtection * data.retainFactor;
  // const dealerProfitRetainedSplitDisplay =
  //   shouldShowDealerProfitSplit && !isNil(proratedRetainedAmount)
  //     ? `${formatCurrency(protectedAmount)} + ${formatCurrency(proratedRetainedAmount)}`
  //     : undefined;

  const dealerProtectProfitAmount = (data?.refundConfigTemplateJson ?? data?.refundConfigTemplate)
    ?.dealerProtectProfitAmount;
  const rowLabels: Record<RowType, string> = {
    dealerCost: lang.dealerCost,
    dealerASCProfit: lang.dealerProfit,
    dealerProfit: lang.dealerResponsiblePortion,
    retailSalePrice: lang.retailSalePrice,
  };

  const renderLabel = (rowType: RowType) => {
    const action = actions?.[rowType];
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{rowLabels[rowType]}</span>
        {action ? <span style={{ marginLeft: '8px', display: 'inline-flex', gap: '6px' }}>{action}</span> : null}
      </div>
    );
  };

  return (
    <Card
      subtitle={() => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ alignSelf: 'flex-start' }}>{lang.contractSummaryAmounts}</p>
              {onAdminFeeExclusion && (
                <CheckWrapper style={{ display: 'flex', gap: Theme.Size.XXS }}>
                  <CheckInput
                    name="excludeAdministrationFee"
                    label={lang.removeAdminFee}
                    class={inputStyles.check.switch}
                    value={data.excludeAdministrationFee}
                    onChange={onAdminFeeExclusion}
                    htmlElementProps={{ title: data.excludeAdministrationFee ? lang.disable : lang.enable }}
                    style={{ fontWeight: 'bold' }}
                  />
                </CheckWrapper>
              )}
            </div>
            &nbsp;
            <table className={ThemeTableSimple}>
              <thead>
                <tr>
                  <th></th>
                  <th style={{ textAlign: 'center' }}>{lang.valueAtSales}</th>
                  <th style={{ textAlign: 'center' }}>{lang.valueRetained}</th>
                  <th style={{ textAlign: 'center' }}>{lang.refundAmount}</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ height: '50px' }}>
                  <td style={{ fontWeight: 'bold' }}>{renderLabel('dealerCost')}</td>
                  <td style={{ textAlign: 'center' }}>${roundToStep(data?.dealerCostValueAtSale, 0.01) ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>${data?.retainedDealerCostTotal ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>${data?.refundedDealerCostTotal ?? 'N/A'}</td>
                </tr>
                <tr style={{ height: '50px' }}>
                  <td style={{ fontWeight: 'bold' }}>{renderLabel('dealerASCProfit')}</td>
                  <td style={{ textAlign: 'center' }}>${roundToStep(data?.dealerProfitValueAtSale, 0.01) ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>${data?.retainedASCDealerProfitTotal ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <ExcludedAmount isExcluded={data?.isRefundedASCDealerProfitTotalExcluded}>
                      ${data?.refundedASCDealerProfitTotal ?? 'N/A'}
                    </ExcludedAmount>
                  </td>
                </tr>
                <tr style={{ height: '50px' }}>
                  <td style={{ fontWeight: 'bold' }}>{lang.dealerProtectedProfitAmount}</td>
                  <td></td>
                  <td></td>
                  <td style={{ textAlign: 'center' }}>
                    {isNil(dealerProtectProfitAmount) ? 'N/A' : `-${formatCurrency(dealerProtectProfitAmount)}`}
                  </td>
                </tr>
                <tr style={{ height: '50px' }}>
                  <td style={{ fontWeight: 'bold' }}>{renderLabel('dealerProfit')}</td>
                  <td style={{ textAlign: 'center' }}>${roundToStep(data?.dealerProfitValueAtSale, 0.01) ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>{formatCurrency(data?.retainedDealerProfitTotal)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <ExcludedAmount isExcluded={data?.isRefundedDealerProfitTotalExcluded}>
                      ${data?.refundedDealerProfitTotal ?? 'N/A'}
                    </ExcludedAmount>
                  </td>
                </tr>
                <tr style={{ height: '50px' }}>
                  <td style={{ fontWeight: 'bold' }}>{renderLabel('retailSalePrice')}</td>
                  <td style={{ textAlign: 'center' }}>
                    ${roundToStep(data?.retailSalePriceValueAtSale, 0.01) ?? 'N/A'}
                  </td>
                  <td style={{ textAlign: 'center' }}>${data?.retainedDealerTotal ?? 'N/A'}</td>
                  <td style={{ textAlign: 'center' }}>${data?.refundedDealerTotal ?? 'N/A'}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 'bold' }} colSpan={4}>
                    {lang.totalDeductions}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'normal' }}>{lang.administrationFee}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'normal' }} colSpan={3}>
                    {data?.excludeAdministrationFee ? lang.excluded : `$${data?.totalDeductions ?? 0}`}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'normal' }}>{lang.administrationFeeTax}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'normal' }} colSpan={3}>
                    {data?.excludeAdministrationFee ? lang.excluded : `$${data?.totalDeductionsTaxAmount ?? 0}`}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'normal' }}>{lang.sumOfClaimPreTaxAmountsPaidOnContract}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'normal' }} colSpan={3}>
                    ${data?.sumOfClaimAmountsPaidOnContract ?? 0}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'normal' }}>{lang.sumOfClaimGstHstAmountsPaidOnContract}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'normal' }} colSpan={3}>
                    ${data?.sumOfClaimGstHstAmountsPaidOnContract ?? 0}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'normal' }}>{lang.sumOfClaimPstAmountsPaidOnContract}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'normal' }} colSpan={3}>
                    ${data?.sumOfClaimProvincialSalesTaxAmountsPaidOnContract ?? 0}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>{lang.totalToBeRefunded}</td>
                  <td style={{ textAlign: 'end', fontWeight: 'bold' }} colSpan={3}>
                    {formatSignedCurrency(data?.totalToBeRefunded)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      }}
      class={ThemeCardListItem}
      style={{
        borderRadius: Theme.Size.S,
        cardMedia: {
          width: 0,
        },
        cardInfo: {
          paddingRight: Theme.Size.M,
        },
      }}
    />
  );
};
